import { useState, useEffect } from 'react';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserCog, UserPlus, Trash2, Loader2, ShieldAlert, ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', name: '', is_admin: false });
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const load = () => API.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); API.get('/admin/email-status').then(r => setEmailStatus(r.data)).catch(() => {}); }, []);

  if (!user?.is_admin) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-[60vh] text-center" data-testid="admin-forbidden">
        <ShieldAlert className="w-10 h-10 text-rose-400 mb-3" />
        <p className="text-sm text-muted-foreground">Doar administratorul poate gestiona utilizatorii.</p>
      </div>
    );
  }

  const createUser = async () => {
    if (!form.email || !form.password) { toast.error('Email si parola obligatorii'); return; }
    setSaving(true);
    try {
      await API.post('/admin/users', form);
      toast.success(`Utilizator ${form.email} creat`);
      setForm({ email: '', password: '', name: '', is_admin: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare creare'); }
    finally { setSaving(false); }
  };

  const toggleRole = async (u) => {
    const makeAdmin = !u.is_admin;
    if (!window.confirm(`${makeAdmin ? 'Promovezi' : 'Retrogradezi'} ${u.email} ${makeAdmin ? 'la Admin' : 'la Ofertare'}?`)) return;
    try {
      await API.post('/auth/set-admin', { email: u.email, is_admin: makeAdmin });
      toast.success(`Rol actualizat: ${u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Stergi utilizatorul ${u.email}?`)) return;
    try { await API.delete(`/admin/users/${u.id}`); toast.success('Utilizator sters'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const resetPassword = async (u) => {
    const np = window.prompt(`Parola nouă pentru ${u.email} (minimum 8 caractere). Toate sesiunile vor fi închise:`);
    if (np === null) return;
    if (np.length < 8) { toast.error('Parola trebuie să aibă minimum 8 caractere'); return; }
    try {
      await API.post(`/admin/users/${u.id}/reset-password`, { new_password: np });
      toast.success(`Parolă resetată pentru ${u.email}. Sesiunile au fost revocate.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la resetare'); }
  };

  return (
    <div className="p-8" data-testid="admin-users-page">
      <div className="flex items-center gap-3 mb-2">
        <UserCog className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Utilizatori</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">SAGA Infrastructura SRL. Roluri: <b>Admin</b> (acces complet, inclusiv Utilizatori si Profil Companie) si <b>Ofertare</b> (fara aceste doua module). Doar administratorul are acces la aceasta pagina.</p>

      {['failed', 'rate_limited'].includes(emailStatus?.status) && <div role="alert" className="mb-6 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm text-amber-300" data-testid="admin-email-delivery-warning">Trimiterea emailurilor de recuperare este temporar indisponibilă{emailStatus.status === 'rate_limited' ? ' din cauza limitei furnizorului' : ''}. Poți reseta parolele manual din lista de mai jos.</div>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-card border-white/10 lg:col-span-1 h-fit">
          <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><UserPlus className="w-4 h-4 text-primary" />Adauga Utilizator</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label className="text-xs">Nume</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nume complet" className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="new-user-name" /></div>
            <div><Label className="text-xs">Email *</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@sagainfrastructura.ro" className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="new-user-email" /></div>
            <div><Label className="text-xs">Parola *</Label>
              <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Parola" className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm font-mono-data" data-testid="new-user-password" /></div>
            <div><Label className="text-xs">Rol</Label>
              <Select value={form.is_admin ? 'admin' : 'ofertare'} onValueChange={v => setForm({ ...form, is_admin: v === 'admin' })}>
                <SelectTrigger className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ofertare">Ofertare</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select></div>
            <Button onClick={createUser} disabled={saving} className="w-full" data-testid="create-user-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}Creeaza cont
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-card border-white/10 lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Utilizatori existenti ({users.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50 border border-white/5" data-testid={`user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                  {u.email_delivery_enabled === false && <p className="text-xs text-amber-300 mt-1" data-testid={`user-no-email-${u.id}`}>Doar autentificare · fără căsuță email</p>}
                </div>
                <Badge variant="outline" className={`text-[9px] border-0 ${u.is_admin ? 'bg-primary/15 text-primary' : 'bg-blue-500/10 text-blue-400'}`}>{u.is_admin ? 'Admin' : 'Ofertare'}</Badge>
                <button onClick={() => resetPassword(u)} title="Reseteaza parola"
                  className="p-1 rounded hover:bg-amber-500/10 text-muted-foreground hover:text-amber-400" data-testid={`reset-pass-${u.id}`}>
                  <KeyRound className="w-3.5 h-3.5" />
                </button>
                {u.id !== user.id && (
                  <button onClick={() => toggleRole(u)} title={u.is_admin ? 'Retrogradeaza la Ofertare' : 'Promoveaza la Admin'}
                    className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-primary" data-testid={`toggle-role-${u.id}`}>
                    {u.is_admin ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  </button>
                )}
                {u.id !== user.id && !u.is_admin && (
                  <button onClick={() => delUser(u)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
            {!users.length && <p className="text-xs text-muted-foreground text-center py-6">Niciun utilizator</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
